import { fr } from "@codegouvfr/react-dsfr";
import { tss } from "tss-react/dsfr";
import { LEGAL_TEXTS } from "../../utils/texts/legal";
import type { DiagnosticItem } from "../../utils/types";
import DiagnosticDecrees from "./DiagnosticDecrees";
import DiagnosticIsolationRange from "./DiagnosticIsolationRange";
import DiagnosticSoundClassificationTable from "./DiagnosticSoundClassificationTable";

type DiagnosticLegalInfosProps = {
  diagnosticItem: DiagnosticItem;
};

const DiagnosticLegalInfos = ({
  diagnosticItem,
}: DiagnosticLegalInfosProps) => {
  const { cx, classes } = useStyles();

  const {
    diagnostic: {
      soundclassification_intersections,
      air_intersections,
      isolation_min,
      isolation_max,
    },
  } = diagnosticItem;

  return (
    <div>
      <div className={cx(classes.container)}>
        <h4 className={fr.cx("fr-text--lg", "fr-mb-4v", "fr-mt-4v")}>
          Isolement réglementaire théorique
        </h4>
        <div className={cx(fr.cx("fr-mt-4v", "fr-mb-8v"))}>
          <DiagnosticIsolationRange
            isolation_min={isolation_min}
            isolation_max={isolation_max}
          />
        </div>
        {soundclassification_intersections.length > 0 && (
          <>
            <h4 className={fr.cx("fr-text--lg", "fr-mb-4v", "fr-mt-4v")}>
              Classement sonore
            </h4>
            <div className={cx(classes.section)}>
              <div className={fr.cx("fr-mb-6v")}>
                <DiagnosticSoundClassificationTable
                  intersections={soundclassification_intersections}
                  caption={LEGAL_TEXTS.SOUNDCLASSIFICATION.INTRODUCTION}
                />
              </div>
              <p className={fr.cx("fr-text--sm")}>
                <i>{LEGAL_TEXTS.SOUNDCLASSIFICATION.DETAILS.NOTICE}</i>
              </p>
              <DiagnosticDecrees diagnosticItem={diagnosticItem} />
            </div>
          </>
        )}
        {air_intersections.length > 0 && (
          <>
            <h4 className={fr.cx("fr-text--lg", "fr-mb-4v", "fr-mt-8v")}>
              Plan d'exposition au bruit aérien
            </h4>
            <div className={cx(classes.section)}>
              <p className={fr.cx("fr-mb-0")}>
                La parcelle est situé dans{" "}
                {air_intersections.length > 1
                  ? "plusieurs secteurs"
                  : "un secteur"}{" "}
                du Plan d’Exposition au Bruit de "
                <b>{air_intersections[0].label}</b>" :
              </p>
              <ul>
                {air_intersections.map((intersection, index) => (
                  <li key={index}>
                    zone <b>{intersection.acoustic_zone}</b>
                    {air_intersections.length > 1 && (
                      <>
                        {" sur "}
                        <b>
                          {Math.round(intersection.percent_impacted * 100)}%
                        </b>{" "}
                        de sa surface
                      </>
                    )}
                  </li>
                ))}
              </ul>
              <p className={fr.cx("fr-mb-0")}>
                Code de l’urbanisme dédié au PEB :{" "}
                <a
                  href="https://www.legifrance.gouv.fr/codes/article_lc/LEGIARTI000031210273"
                  target="_blank" rel="noopener"
                >
                  article L112-10 du code de l’urbanisme
                </a>
              </p>
            </div>
          </>
        )}
        {air_intersections.length === 0 &&
          soundclassification_intersections.length === 0 && (
            <p className={cx(classes.section, fr.cx("fr-mb-0"))}>
              {LEGAL_TEXTS.NO_IMPACT}
            </p>
          )}
      </div>
    </div>
  );
};

const useStyles = tss.create(() => ({
  container: {
    ul: {
      marginLeft: fr.spacing("4v"),
      li: {
        lineHeight: "1.75rem",
        "&:not(:last-child)": {
          marginBottom: fr.spacing("2v"),
        },
      },
    },
  },
  section: {
    padding: `${fr.spacing("2v")} ${fr.spacing("2v")} ${fr.spacing(
      "2v"
    )} ${fr.spacing("10v")}`,
    marginLeft: fr.spacing("6v"),
    borderLeft: `4px solid ${fr.colors.decisions.border.default.blueFrance.default}`,
  },
}));

export default DiagnosticLegalInfos;
